import { useEffect, useRef, useState } from "react";
import { MESSAGES_TO_FETCH_PAGE_SIZE } from "@lifeat/shared/rooms/room-chat/RoomChatConstants";
import { ChatMessage, RoomChatCollection } from "@lifeat/shared/rooms/room-chat/RoomChatTypes";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useDbValueListener from "@hooks/useDbValueListener";
import { useEvent } from "@hooks/useEvent";
import { ChatContextType, useChatContext } from "./ChatContext";
import { ChatMessagesContextType, useChatMessagesContext } from "./ChatMessagesContext";
import { getLatestChatMessagesRef, getPinnedMessagesRef } from "./RoomChatService";

export default function useRoomChatMessages(roomId: string): [number, () => void] {
    const { user }: UserContextType = useUserContext();

    const { setPinnedMessage }: ChatContextType = useChatContext();
    const { latestMessages, setLatestMessages }: ChatMessagesContextType = useChatMessagesContext();

    const initMessageListeners = useRef(false);

    const pinnedMessageId = useDbValueListener(RoomChatCollection.at(roomId).at("pinnedMessage").node);

    const [messagesPageSize, setMessagesPageSize] = useState(1);
    const increaseMessagesPageSize = () => {
        setMessagesPageSize(messagesPageSize + 1);
        initMessageListeners.current = false;
    };

    const shouldBeHidden = (msg: ChatMessage) => {
        return (msg.hidden && msg.authorUid !== user.uid) || msg.reporters?.includes(user?.uid);
    };

    // useEvent hook so the function has access to prop and state (access to latest value of user)
    const addNewMessageToList = useEvent((newMsgId: string, newMsg: ChatMessage) => {
        if (latestMessages[newMsg?.uuid]) {
            let updateMsgs = { ...latestMessages };
            delete updateMsgs[newMsg?.uuid];
            setLatestMessages({ ...updateMsgs, [newMsgId]: newMsg });
        } else if (!latestMessages[newMsgId] && !shouldBeHidden(newMsg)) {
            const updatedMsgs = { ...latestMessages, [newMsgId]: newMsg };
            setLatestMessages(updatedMsgs);
        }
    });

    const removeMessageFromList = useEvent((msgId: string) => {
        if (latestMessages[msgId]) {
            const updatedMsgs = { ...latestMessages };
            delete updatedMsgs[msgId];
            setLatestMessages(updatedMsgs);
        }
    });

    const handleMessageVisibilityOnChange = useEvent((msgId: string, newMsg: ChatMessage) => {
        const messageExistsInList = !!latestMessages[msgId];

        // Update message list based on if message is hidden or reported by user
        if (messageExistsInList && shouldBeHidden(newMsg)) {
            removeMessageFromList(msgId);
        } else if (!messageExistsInList && !shouldBeHidden) {
            addNewMessageToList(msgId, newMsg);
        }
    });

    const handlePinnedMessageChanged = useEvent((msgId: string | undefined, msg: ChatMessage | undefined) => {
        // handle removing the pinned message if it has been reported by user
        if (msg && !shouldBeHidden(msg)) {
            setPinnedMessage({ ...msg, ...{ id: msgId } });
        } else {
            setPinnedMessage(null);
        }
    });

    useEffect(() => {
        if (roomId && !initMessageListeners.current) {
            initMessageListeners.current = true;

            const latestRoomChatMessagesRef = getLatestChatMessagesRef(
                roomId,
                MESSAGES_TO_FETCH_PAGE_SIZE * messagesPageSize,
            );

            const newMessagesListener = latestRoomChatMessagesRef.on("child_added", (data) => {
                if (data.exists()) {
                    const msgId = data.key;
                    const msg = data.val() as ChatMessage;
                    if (msgId && msg) {
                        addNewMessageToList(msgId, msg);
                    }
                }
            });

            const removedMessagesListener = latestRoomChatMessagesRef.on("child_removed", (data) => {
                if (data.exists()) {
                    const msgId = data.key;
                    if (msgId) {
                        removeMessageFromList(msgId);
                    }
                }
            });

            const messagesChangedListener = latestRoomChatMessagesRef.on("child_changed", (data) => {
                if (data.exists()) {
                    const msgId = data.key;
                    const msg = data.val() as ChatMessage;
                    if (msgId && msg) {
                        handleMessageVisibilityOnChange(msgId, msg);
                    }
                }
            });

            return () => {
                latestRoomChatMessagesRef.off("child_added", newMessagesListener);
                latestRoomChatMessagesRef.off("child_removed", removedMessagesListener);
                latestRoomChatMessagesRef.off("child_changed", messagesChangedListener);
            };
        }
        return () => {};
    }, [roomId, messagesPageSize]);

    useEffect(() => {
        if (pinnedMessageId) {
            const pinnedMessageRef = getPinnedMessagesRef(roomId, pinnedMessageId);

            const pinnedMessageListener = pinnedMessageRef.on("value", (data) => {
                handlePinnedMessageChanged(data.key, data.val());
            });

            return () => {
                pinnedMessageRef.off("value", pinnedMessageListener);
            };
        } else {
            setPinnedMessage(null);
        }
        return () => {};
    }, [pinnedMessageId]);

    return [messagesPageSize, increaseMessagesPageSize];
}
